import { z } from 'zod';

/**
 * Cortex Room Protocol frames: the plain UTF-8 JSON messages the transcription
 * bot sends into the ODIN room as an ordinary peer message. This is Cortex's
 * wire format, not andor's API, so it is validated here rather than in
 * `@andor/contracts`. Only the parts the client reads are checked; unknown
 * fields pass through untouched.
 *
 * Envelope: `{ cortex: 2, type, id, seq, data }`. `seq` counts the frames of
 * one bot connection; `type` names the payload in `data`.
 */
export const CORTEX_ROOM_PROTOCOL_VERSION = 2;

/** The frame type that carries one finished transcript line. */
export const CORTEX_TRANSCRIPT_FRAME = 'cortex.transcript';

export const cortexRoomFrameSchema = z.object({
  cortex: z.literal(CORTEX_ROOM_PROTOCOL_VERSION),
  type: z.string().min(1).max(64),
  id: z.string().min(1).max(64),
  seq: z.number().int().min(1),
  data: z.unknown(),
});

export type CortexRoomFrame = z.infer<typeof cortexRoomFrameSchema>;

/**
 * `data` of a `cortex.transcript` frame. `segmentId` is the Cortex message id,
 * the same id the transcript backlog returns, and `ts` the same message
 * timestamp, so frames and backlog dedupe and sort together.
 */
export const cortexTranscriptDataSchema = z.object({
  segmentId: z.string().min(1).max(128),
  senderName: z.string().max(256),
  text: z.string().max(8192),
  ts: z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'Invalid timestamp'),
  /** A segment still being transcribed; Cortex sends only final ones today. */
  interim: z.boolean().default(false),
});

export type CortexTranscriptData = z.infer<typeof cortexTranscriptDataSchema>;

/** The frame a decoded peer message carries, or `null` if it is none. */
export function parseCortexRoomFrame(value: unknown): CortexRoomFrame | null {
  const parsed = cortexRoomFrameSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * The finished transcript segment a frame carries, or `null` for any other
 * frame, an interim segment or malformed data.
 */
export function parseCortexTranscript(frame: CortexRoomFrame): CortexTranscriptData | null {
  if (frame.type !== CORTEX_TRANSCRIPT_FRAME) return null;
  const parsed = cortexTranscriptDataSchema.safeParse(frame.data);
  return parsed.success && !parsed.data.interim ? parsed.data : null;
}
